import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { API_BASE_URL } from '../config/api';

/**
 * Opens an SSE connection to GET /events and invalidates React Query caches
 * when the server pushes a colis_statut_updated event.
 * Automatically reconnects with exponential back-off on failure.
 */
export function useSSE(enabled: boolean) {
  const qc = useQueryClient();
  const retryDelay = useRef(1_000);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!enabled) return;

    function connect() {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const url = `${API_BASE_URL}/events?token=${encodeURIComponent(token)}`;
      const es = new EventSource(url);
      esRef.current = es;

      es.onopen = () => {
        retryDelay.current = 1_000; // reset back-off on success
      };

      es.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.type === 'colis_statut_updated') {
            qc.invalidateQueries({ queryKey: ['colis'] });
          }
        } catch {
          // ignore malformed messages
        }
      };

      es.onerror = () => {
        es.close();
        esRef.current = null;
        // Exponential back-off capped at 30 s
        timeoutRef.current = setTimeout(() => {
          retryDelay.current = Math.min(retryDelay.current * 2, 30_000);
          connect();
        }, retryDelay.current);
      };
    }

    connect();

    return () => {
      esRef.current?.close();
      esRef.current = null;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [enabled, qc]);
}
